import React, { useState, useEffect } from 'react';

const HINT_SEEN_KEY = 'sonmi_add_to_list_hint_seen';
const SUCCESS_HINT_SEEN_KEY = 'sonmi_add_to_list_success_hint_seen';

const ProductSaveButtons = ({ productImage, productName, productBrand, onSave, customListNames, createCustomList = null }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [savedTo, setSavedTo] = useState(null);
  // Hotfix D: inline create-new-list flow inside the dropdown.
  // creatingNew=true → swap the dropdown body for a name input + Save.
  // createListError surfaces e.g. "no slots free" or DB write failure.
  const [creatingNew, setCreatingNew] = useState(false);
  const [newListNameInput, setNewListNameInput] = useState('');
  const [createListError, setCreateListError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [hintSeen, setHintSeen] = useState(() => {
    try {
      return localStorage.getItem(HINT_SEEN_KEY) === 'true';
    } catch (_) {
      return false;
    }
  });
  const [successHintSeen, setSuccessHintSeen] = useState(() => {
    try {
      return localStorage.getItem(SUCCESS_HINT_SEEN_KEY) === 'true';
    } catch (_) {
      return false;
    }
  });

  const handleSave = (category) => {
    const product = {
      image: productImage,
      name: productName,
      brand: productBrand,
      savedAt: new Date().toISOString()
    };
    onSave(product, category);
    setSavedTo(category);
    setShowDropdown(false);
    setCreatingNew(false);
    setNewListNameInput('');
    setCreateListError('');
  };

  // Hotfix D: create-and-save flow. Two failure modes:
  //   - createCustomList returns null → both custom slots full (cap is 2).
  //   - The async upsert may have failed (alert is raised inside
  //     createCustomList itself). Local state still updates so we proceed
  //     with the save; the user has been warned by the alert.
  const handleCreateAndSave = async () => {
    setCreateListError('');
    const trimmed = newListNameInput.trim();
    if (!trimmed) {
      setCreateListError('Name cannot be empty.');
      return;
    }
    if (trimmed.length > 50) {
      setCreateListError('Name must be 50 characters or fewer.');
      return;
    }
    if (!createCustomList) {
      setCreateListError('Cannot create lists right now.');
      return;
    }
    setIsCreating(true);
    try {
      const newKey = await createCustomList(trimmed);
      if (!newKey) {
        setCreateListError("You've reached the limit (2 custom lists). Delete one to add a new list.");
        return;
      }
      handleSave(newKey);
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (savedTo && !successHintSeen) {
      const t = setTimeout(() => {
        setSuccessHintSeen(true);
        try {
          localStorage.setItem(SUCCESS_HINT_SEEN_KEY, 'true');
        } catch (_) {}
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [savedTo, successHintSeen]);

  const listLabel = (cat) =>
    cat === 'favourites' ? 'Favourites' :
    cat === 'current' ? 'Current products' :
    cat === 'wishlist' ? 'Wishlist' :
    cat === 'custom1' ? (customListNames?.custom1 || 'Custom List 1') :
    cat === 'custom2' ? (customListNames?.custom2 || 'Custom List 2') : 'List';

  if (savedTo) {
    return (
      <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200 space-y-1">
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-green-700 text-xs font-medium">
            Added to {listLabel(savedTo)}
          </span>
        </div>
        {!successHintSeen && (
          <p className="text-green-600/80 text-xs pl-6">View all in Menu → Product catalog.</p>
        )}
      </div>
    );
  }

  const handleToggleDropdown = () => {
    if (!showDropdown) {
      try {
        localStorage.setItem(HINT_SEEN_KEY, 'true');
      } catch (_) {}
      setHintSeen(true);
    }
    setShowDropdown(!showDropdown);
  };

  return (
    <div className="relative mt-2">
      <button
        onClick={handleToggleDropdown}
        className="flex items-center gap-2 px-3 py-2 bg-[#F3D5B5] hover:bg-[#F3D5B5]/80 text-[#2A324B] rounded-lg text-xs font-medium transition-colors"
      >
        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
        Add to list
        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {!hintSeen && (
        <p className="text-[#2A324B]/60 text-xs mt-1.5">Saved items are in Menu → Product catalog.</p>
      )}

      {showDropdown && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-[#341C1C]/10 z-50">
          <button
            onClick={() => handleSave('favourites')}
            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F2EAE1]/50 text-[#2A324B] text-xs"
          >
            <div className="w-4 h-4 bg-red-100 rounded flex items-center justify-center">
              <svg className="h-3 w-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
              </svg>
            </div>
            Favourites
          </button>
          
          <button
            onClick={() => handleSave('current')}
            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F2EAE1]/50 text-[#2A324B] text-xs"
          >
            <div className="w-4 h-4 bg-green-100 rounded flex items-center justify-center">
              <svg className="h-3 w-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            Current products
          </button>
          
          <button
            onClick={() => handleSave('wishlist')}
            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F2EAE1]/50 text-[#2A324B] text-xs"
          >
            <div className="w-4 h-4 bg-blue-100 rounded flex items-center justify-center">
              <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            Wishlist
          </button>

          {/* Custom Lists */}
          {customListNames?.custom1 && customListNames.custom1 !== 'My Custom List 1' && (
            <button
              onClick={() => handleSave('custom1')}
              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F2EAE1]/50 text-[#2A324B] text-xs"
            >
              <div className="w-4 h-4 bg-blue-100 rounded flex items-center justify-center">
                <svg className="h-3 w-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              {customListNames.custom1}
            </button>
          )}

          {customListNames?.custom2 && customListNames.custom2 !== 'My Custom List 2' && (
            <button
              onClick={() => handleSave('custom2')}
              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F2EAE1]/50 text-[#2A324B] text-xs"
            >
              <div className="w-4 h-4 bg-green-100 rounded flex items-center justify-center">
                <svg className="h-3 w-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              {customListNames.custom2}
            </button>
          )}

          {/* Hotfix D: Create-new-list inline. Only show when we have a
              createCustomList prop AND at least one custom slot is free. */}
          {createCustomList && (
            (!customListNames?.custom1 || customListNames.custom1 === 'My Custom List 1' ||
             !customListNames?.custom2 || customListNames.custom2 === 'My Custom List 2') && (
              creatingNew ? (
                <div className="px-3 py-2 border-t border-[#341C1C]/10 space-y-2">
                  <input
                    type="text"
                    value={newListNameInput}
                    onChange={(e) => { setNewListNameInput(e.target.value); setCreateListError(''); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateAndSave();
                      if (e.key === 'Escape') { setCreatingNew(false); setNewListNameInput(''); setCreateListError(''); }
                    }}
                    maxLength={50}
                    autoFocus
                    placeholder="New list name"
                    className="w-full px-2 py-1.5 text-xs border border-[#341C1C]/20 rounded focus:outline-none focus:ring-1 focus:ring-[#F3D5B5]"
                  />
                  {createListError && (
                    <p className="text-[13px] text-amber-700">{createListError}</p>
                  )}
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => { setCreatingNew(false); setNewListNameInput(''); setCreateListError(''); }}
                      disabled={isCreating}
                      className="flex-1 px-2 py-1 text-xs text-[#2A324B]/70 rounded border border-[#341C1C]/15 hover:bg-gray-50 disabled:opacity-50"
                    >Cancel</button>
                    <button
                      onClick={handleCreateAndSave}
                      disabled={isCreating || !newListNameInput.trim()}
                      className="flex-1 px-2 py-1 text-xs bg-[#F3D5B5] text-[#2A324B] rounded hover:bg-[#F3D5B5]/80 disabled:opacity-50"
                    >{isCreating ? 'Saving…' : 'Create & save'}</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setCreatingNew(true); setNewListNameInput(''); setCreateListError(''); }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#F2EAE1]/50 text-[#2A324B]/70 text-xs border-t border-[#341C1C]/10"
                >
                  <div className="w-4 h-4 rounded flex items-center justify-center border border-[#2A324B]/30">
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  Create new list
                </button>
              )
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSaveButtons;
